import {
	RestaurantStatusDTO,
	MapDTO,
	SaleCenterStatusDTO
} from 'core/api';
import { MapImageCache } from 'core/ui';


type Selection =
	{ mode: 'maps', selectedMap: MapDTO } |
	{ mode: 'sale-centers', selectedSaleCenter: SaleCenterStatusDTO };

export class SaleLocationStatus {

	// Hace falta mantener la selección porque al desmontarse
	// el componente queremos mantener el estado para la próxima ejecución
	private selection: Nullable<Selection> = null;
	private lastSelectedMapId: Nullable<number> = null;
	private lastSelectedSaleCenterId: Nullable<number> = null;
	private status: Nullable<RestaurantStatusDTO> = null;
	private cache: Nullable<MapImageCache> = null;
	private onChange: (selection: Selection) => void = () => { };

	constructor(private getRestaurantStatus: () => Promise<RestaurantStatusDTO>) {
	}

	initialize = async (): Promise<void> => {

		const result = await Promise.all([this.getRestaurantStatus(), this.cache ? Promise.resolve(this.cache) : MapImageCache.load()]);

		this.status = result[0];
		this.cache = result[1];

		if (this.selection === null) {
			await this.updateSelection(this.status.Maps.length
				? { mode: 'maps', selectedMap: this.status.Maps[0] }
				: { mode: 'sale-centers', selectedSaleCenter: this.status.SaleCenters[0] });
			return;
		}

		if (this.selection.mode === 'maps') {
			const previousMap = this.selection.selectedMap;
			await this.updateSelection({ mode: 'maps', selectedMap: this.status.Maps.find(x => x.Id === previousMap.Id) || this.status.Maps[0] });
			return;
		}

		const previousSaleCenter = this.selection.selectedSaleCenter;
		await this.updateSelection({ mode: 'sale-centers', selectedSaleCenter: this.status.SaleCenters.find(x => x.Id === previousSaleCenter.Id) || this.status.SaleCenters[0] });
	};

	getAllSaleCenters = (): SaleCenterStatusDTO[] => {
		return this.status ? this.status.SaleCenters : [];
	};

	getAllMaps = (): MapDTO[] => {
		return this.status ? this.status.Maps : [];
	};

	selectMap = async (mapId?: number) => {
		this.status = await this.getRestaurantStatus();
		const selectedMap = this.status.Maps.find(x => x.Id === (mapId ?? this.lastSelectedMapId)) ?? this.status.Maps[0];
		await this.updateSelection({ mode: 'maps', selectedMap });
	};

	selectSaleCenter = async (saleCenterId?: number): Promise<void> => {
		this.status = await this.getRestaurantStatus();
		const selectedSaleCenter = this.status.SaleCenters.find(x => x.Id === (saleCenterId ?? this.lastSelectedSaleCenterId)) ?? this.status.SaleCenters[0];
		await this.updateSelection({ mode: 'sale-centers', selectedSaleCenter: selectedSaleCenter });
	};

	private updateSelection = async (newSelection: Selection): Promise<void> => {
		this.selection = newSelection;

		this.lastSelectedMapId = newSelection.mode === 'maps' ? newSelection.selectedMap.Id : this.lastSelectedMapId;
		this.lastSelectedSaleCenterId = newSelection.mode === 'sale-centers' ? newSelection.selectedSaleCenter.Id : this.lastSelectedSaleCenterId;

		// No lo hacemos con Events porque se reutiliza en la pantalla de mover tickets y seleccionar ubicación
		// y no queremos que se lance el mismo evento porque estaría cambiando el estado de las dos pantallas.
		this.onChange(this.selection);
	};

	public registerOnChange = (action: (selection: Selection) => void): void => {
		this.onChange = action;
	};

	public unregisterOnChanged = () => {
		this.onChange = () => { };
	};

	getSelection = (): Selection => {

		if (this.selection === null)
			throw new Error('Debe inicializarse la selección');

		return this.selection;
	};

	getImageCache = (): MapImageCache => {

		if (this.cache === null)
			throw new Error('Debe inicializarse la cache');

		return this.cache;
	};

	static create(getRestaurantStatus: () => Promise<RestaurantStatusDTO>): SaleLocationStatus {
		return new SaleLocationStatus(getRestaurantStatus);
	}
}



